import {createContext, FC, useContext, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  WithChildren,
  createResponseContext,
  initialQueryResponse,
  initialQueryState,
  stringifyRequestQuery,
  PaginationState,
} from '../../../../_metronic/helpers'
import {DownloadRequestItem, DownloadRequestQueryResponse} from './_models'
import {useQueryRequest} from './QueryRequestProvider'
import {useConfig} from '../ConfigContext'
import {createGetDownloadRequests} from './_requests'

const QueryResponseContext = createResponseContext<DownloadRequestItem>(initialQueryResponse)

const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  const {isKpi} = useConfig()

  const query = useMemo(() => stringifyRequestQuery(state), [state])

  const getDownloadRequests = useMemo(
    () => createGetDownloadRequests(isKpi),
    [isKpi]
  )

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery<DownloadRequestQueryResponse>(
    [`download-requests-${isKpi ? 'kpi' : 'reports'}`, query],
    () => getDownloadRequests(query),
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  return (
    <QueryResponseContext.Provider value={{isLoading: isFetching, refetch, response, query}}>
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)

const useQueryResponseData = (): DownloadRequestItem[] => {
  const {response} = useQueryResponse()
  return response?.data ?? []
}

const useQueryResponsePagination = (): PaginationState & {current_page: number} => {
  const {response} = useQueryResponse()
  return response?.meta ?? {...initialQueryState, current_page: 1}
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
}
